import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import ReCAPTCHA from 'react-google-recaptcha';
import { reportService } from '../services/api';
import RECAPTCHA_CONFIG from '../utils/recaptchaConfig';
import AttachmentUploader from '../components/AttachmentUploader';

const validationSchema = Yup.object().shape({
  contractorName: Yup.string().required('Contractor name is required'),
  projectLocation: Yup.string().required('Project location is required'),
  reportDetails: Yup.string()
    .min(20, 'Report details must be at least 20 characters')
    .max(5000, 'Report details cannot exceed 5000 characters')
    .required('Report details are required'),
  fullName: Yup.string(), // Optional field
  email: Yup.string().email('Invalid email address'), // Optional but must be valid if provided
  phone: Yup.string(), // Optional field
  privacyConsent: Yup.boolean().when('email', {
    is: (email) => email && email.length > 0,
    then: (schema) => schema.oneOf([true], 'You must agree to the privacy policy when providing contact information'),
    otherwise: (schema) => schema.notRequired()
  }),
  honeypot: Yup.string().max(0, 'Security validation failed'), // Honeypot field
  recaptcha: Yup.string().required('Please complete the reCAPTCHA verification')
});

const ReportFormPage = () => {
  const [files, setFiles] = useState([]);
  const [recaptchaToken, setRecaptchaToken] = useState(null);
  const navigate = useNavigate();

  // reCAPTCHA configuration
  const RECAPTCHA_SITE_KEY = RECAPTCHA_CONFIG.SITE_KEY;

  const handleRecaptchaChange = (token) => {
    setRecaptchaToken(token);
  };

  const handleSubmit = async (values, { setSubmitting, setFieldError }) => {
    try {
      setSubmitting(true);

      // Validate reCAPTCHA
      if (!recaptchaToken) {
        setFieldError('recaptcha', 'Please complete the reCAPTCHA verification');
        return;
      }

      // Prepare form data
      const formData = new FormData();
      formData.append('contractor_name', values.contractorName);
      formData.append('project_location', values.projectLocation);
      formData.append('report_details', values.reportDetails);

      // Always include contact information (backend will determine anonymity)
      formData.append('complainant_name', values.fullName || '');
      formData.append('complainant_email', values.email || '');
      formData.append('complainant_phone', values.phone || '');

      // Properly handle boolean for privacy consent
      if (values.privacyConsent) {
        formData.append('data_privacy_consent', 'true');
      } else {
        formData.append('data_privacy_consent', 'false');
      }

      // Add security fields
      formData.append('recaptcha_token', recaptchaToken);
      formData.append('honeypot', values.honeypot || '');

      // Add files
      files.forEach(file => {
        formData.append('files[]', file);
      });

      // Submit to the backend using our API service
      const response = await reportService.submitReport(formData);

      // Get the reference ID from the backend response
      const referenceId = response.data.data?.reference_id || response.data.referenceId;

      // Show success notification
      if (window.showToast) {
        window.showToast('Report submitted successfully! You will be redirected to the confirmation page.', 'success');
      }

      // Redirect to confirmation page
      navigate(`/confirmation/${referenceId}`);
    } catch (error) {
      console.error('Error submitting report:', error);

      if (error.response?.status === 422) {
        // Handle validation errors
        const errors = error.response.data.errors;
        Object.keys(errors).forEach(field => {
          setFieldError(field, errors[field][0]);
        });

        // Show validation error toast
        if (window.showToast) {
          window.showToast('Please fix the validation errors and try again.', 'error');
        }
      } else if (error.response?.status === 429) {
        // Rate limiting
        if (window.showToast) {
          window.showToast('Too many submissions. Please try again later.', 'warning');
        } else {
          alert('Too many submissions. Please try again later.');
        }
      } else if (error.response?.status === 400) {
        // Bad request - usually reCAPTCHA failure
        const message = error.response.data.message || 'Invalid request. Please check all fields and try again.';
        if (window.showToast) {
          window.showToast(message, 'error');
        } else {
          alert(message);
        }
      } else if (error.response?.status === 500) {
        // Server error
        if (window.showToast) {
          window.showToast('Server error occurred. Please try again or contact support if the problem persists.', 'error');
        } else {
          alert('Server error occurred. Please try again or contact support if the problem persists.');
        }
      } else {
        // General error
        const message = error.response?.data?.message || 'There was an error submitting your report. Please try again.';
        if (window.showToast) {
          window.showToast(message, 'error');
        } else {
          alert(message);
        }
      }
    } finally {
      setSubmitting(false);
    }
  };  return (
    <div className="max-w-3xl mx-auto">
      <h1 className="text-3xl font-bold text-primary mb-8">Submit a Report</h1>

      <Formik
        initialValues={{
          contractorName: '',
          projectLocation: '',
          reportDetails: '',
          fullName: '',
          email: '',
          phone: '',
          privacyConsent: false,
          honeypot: '',
          recaptcha: ''
        }}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ isSubmitting, values }) => (
          <Form className="space-y-6">
            {/* Honeypot field (hidden) */}
            <Field
              type="text"
              name="honeypot"
              style={{ display: 'none' }}
              tabIndex="-1"
              autoComplete="off"
            />

            <div className="bg-white p-6 rounded-lg shadow-md">
              <h2 className="text-xl font-bold text-primary mb-4">Contractor Information</h2>

              <div className="mb-4">
                <label htmlFor="contractorName" className="form-label">Name of the Contractor / Firm</label>
                <Field name="contractorName" type="text" className="form-input" />
                <ErrorMessage name="contractorName" component="div" className="text-red-500 text-sm mt-1" />
              </div>

              <div className="mb-4">
                <label htmlFor="projectLocation" className="form-label">Project Location</label>
                <Field name="projectLocation" type="text" className="form-input" />
                <ErrorMessage name="projectLocation" component="div" className="text-red-500 text-sm mt-1" />
              </div>

              <div className="mb-4">
                <label htmlFor="reportDetails" className="form-label">Details of the Report</label>
                <Field as="textarea" name="reportDetails" rows="4" className="form-input" />
                <ErrorMessage name="reportDetails" component="div" className="text-red-500 text-sm mt-1" />
              </div>

              <div>
                <label className="form-label">Upload Supporting Files (Photo or PDF)</label>
                <AttachmentUploader
                  files={files}
                  setFiles={setFiles}
                  maxFiles={5}
                  maxSize={10485760} // 10MB
                />
              </div>
            </div>

            {/* Complainant's Information (Optional) */}
            <div className="bg-white p-6 rounded-lg shadow-md">
              <h2 className="text-xl font-bold text-primary mb-4">Your Contact Information (Optional)</h2>
              <p className="text-sm text-gray-600 mb-4">
                Providing your contact information is optional but may help us follow up on your report if needed.
                Reports can be submitted anonymously if you prefer.
              </p>

              <div className="mb-4">
                <label htmlFor="fullName" className="form-label">Full Name (Optional)</label>
                <Field name="fullName" type="text" className="form-input" />
                <ErrorMessage name="fullName" component="div" className="text-red-500 text-sm mt-1" />
              </div>

              <div className="mb-4">
                <label htmlFor="email" className="form-label">Email Address (Optional)</label>
                <Field name="email" type="email" className="form-input" />
                <ErrorMessage name="email" component="div" className="text-red-500 text-sm mt-1" />
              </div>

              <div className="mb-4">
                <label htmlFor="phone" className="form-label">Phone Number (Optional)</label>
                <Field name="phone" type="tel" className="form-input" />
                <ErrorMessage name="phone" component="div" className="text-red-500 text-sm mt-1" />
              </div>

              <div>
                <label className="flex items-start">
                  <Field name="privacyConsent" type="checkbox" className="mt-1 mr-2" />
                  <span className="text-sm">
                    I consent to the collection, use, and processing of the personal information I provided in
                    accordance with the Data Privacy Act of 2012. I understand that my information will be used
                    solely for the purpose of this report.
                  </span>
                </label>
                <ErrorMessage name="privacyConsent" component="div" className="text-red-500 text-sm mt-1" />
              </div>
            </div>

            {/* reCAPTCHA Section */}
            <div className="bg-white p-6 rounded-lg shadow-md">
              <h2 className="text-xl font-bold text-primary mb-4">Security Verification</h2>
              <p className="text-sm text-gray-600 mb-4">
                Please complete the security verification below to submit your report.
              </p>


              <Field name="recaptcha">
                {({ field, form }) => (
                  <div>
                    <ReCAPTCHA
                      sitekey={RECAPTCHA_SITE_KEY}
                      onChange={(token) => {
                        handleRecaptchaChange(token);
                        form.setFieldValue('recaptcha', token || '');
                      }}
                      onExpired={() => {
                        setRecaptchaToken(null);
                        form.setFieldValue('recaptcha', '');
                      }}
                    />
                    <ErrorMessage name="recaptcha" component="div" className="text-red-500 text-sm mt-2" />
                  </div>
                )}
              </Field>
            </div>

            <div className="text-center">
              <button
                type="submit"
                disabled={isSubmitting}
                className="btn-primary text-lg px-8 py-3"
              >
                {isSubmitting ? 'Submitting...' : 'Submit Report'}
              </button>
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );
};

export default ReportFormPage;
